import { api, apiRequest } from "./api";

export interface StaffListParams {
  page?: number;
  page_size?: number;
  role?: string;
  search?: string;
}

export interface ConsoleStaffRole {
  name: string;
  label: string;
}

export interface StaffMember {
  id: string;
  phone_number: string;
  email: string | null;
  console_user_profile__designation: string | null;
  console_user_profile__experience: number | null;
  console_user_profile__meta: Record<string, any> | null;
  user_type__name: string;
  first_name: string;
  last_name: string;
  profile_pic: string | null;
  is_active: boolean;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface CreateStaffResponse {
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    zarity_user_id: string;
    profile_pic: string | null;
  };
  designation: string;
}

export interface StaffDetail {
  id: string;
  user_type: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  profile_pic: string;
  user_profile: {
    id?: number;
    user_id?: string;
    meta: {
      about?: string;
      languages_known?: string[];
      previous_experience?: Array<{
        institution_name: string;
        designation: string;
        location: string;
        start_date: string;
        end_date: string;
      }>;
      education?: Array<{
        institution_name: string;
        degree: string;
        location: string;
        start_year: string;
        end_year: string;
      }>;
      certifications?: Array<{
        title: string;
        year: string;
        school_name: string;
        location: string;
        certificate_link: string;
      }>;
    };
    gender: string | null;
    experience: number | null;
    designation: string | null;
    dob: string | null;
    [key: string]: any;
  };
  slots: Array<{
    id: number;
    day: string;
    start_time: string;
    end_time: string;
    location: string;
    mode?: string;
    clinic_id?: string | null;
  }>;
}

export const staffAPI = {
  /** GET /internal/users/rbac/roles/ for canonical Console staff roles */
  listRoles: (): Promise<ConsoleStaffRole[]> => {
    return apiRequest(() => api.get("/internal/users/rbac/roles/"));
  },

  /** GET /internal/users/staff/ with optional pagination, role filter, and search */
  listStaff: (
    params?: StaffListParams
  ): Promise<PaginatedResponse<StaffMember>> => {
    return apiRequest(() => api.get("/internal/users/staff/", { params }));
  },

  /** POST /internal/users/ to create a new staff member */
  createStaff: (formData: FormData): Promise<CreateStaffResponse> => {
    return apiRequest(() =>
      api.post("/internal/users/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    );
  },

  /** PUT /internal/users/<id>/staff/ to toggle is_active */
  toggleStaffStatus: (
    staffId: string,
    isActive: boolean
  ): Promise<{ success: string }> => {
    return apiRequest(() =>
      api.put(`/internal/users/${staffId}/staff/`, { is_active: isActive })
    );
  },

  /** GET /internal/users/<id>/ to fetch full staff detail */
  fetchStaffDetail: (id: string): Promise<StaffDetail> => {
    return apiRequest(() => api.get(`/internal/users/${id}/`));
  },

  /** PUT /internal/users/<id>/ to update an existing staff member */
  updateStaff: (
    id: string,
    formData: FormData
  ): Promise<{ success: string }> => {
    return apiRequest(() =>
      api.put(`/internal/users/${id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    );
  },
};
